<template>
  <v-dialog
    :value="value"
    persistent
    max-width="380"
    @input="$emit('input', $event)"
  >
    <!-- Paso 1: Intercepción -->
    <v-card v-if="paso === 1" class="pa-6 text-center rounded-xl">
      <v-icon size="52" color="warning" class="mb-3"
        >mdi-alert-circle-outline</v-icon
      >
      <h2 class="font-weight-bold mb-2" style="font-size: 1.2rem">
        ¿Deseas salir?
      </h2>
      <p class="grey--text text--darken-1 text-body-2 mb-6">
        Estás en el paso {{ pasoActual }} de {{ totalPasos }}. Si sales ahora
        tendrás que comenzar el proceso desde el inicio.
      </p>
      <v-btn
        block
        color="success"
        large
        rounded
        elevation="0"
        class="mb-3 font-weight-bold text-capitalize"
        @click="$emit('input', false)"
      >
        Continuar mi solicitud
      </v-btn>
      <v-btn
        text
        color="grey darken-1"
        class="text-capitalize"
        @click="seleccionarModo('sinGuardar')"
      >
        Salir de todas formas
      </v-btn>
    </v-card>

    <!-- Paso 2: Encuesta -->
    <v-card v-else-if="paso === 2" class="rounded-xl">
      <div class="d-flex justify-end pa-2">
        <v-btn icon small @click="cerrar">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <div class="px-5 pb-5">
        <h3 class="font-weight-bold mb-1">Antes de irte</h3>
        <p class="text-body-2 grey--text text--darken-1 mb-4">
          ¿Nos ayudas con una pregunta? Toma solo unos segundos.
        </p>
        <p class="text-body-2 font-weight-medium mb-2">
          ¿Cuál fue la razón principal por la que no terminaste tu solicitud?
        </p>
        <v-radio-group v-model="razonSeleccionada" class="mt-0" hide-details>
          <v-radio
            v-for="razon in razones"
            :key="razon"
            :label="razon"
            :value="razon"
            color="success"
            class="mb-1"
          />
        </v-radio-group>
        <v-btn
          block
          color="success"
          large
          rounded
          elevation="0"
          class="mt-5 font-weight-bold text-capitalize"
          :disabled="!razonSeleccionada"
          @click="
            razonSeleccionada === 'Otra razón' ? (paso = 4) : enviarEncuesta()
          "
        >
          {{ razonSeleccionada === 'Otra razón' ? 'Siguiente' : 'Enviar' }}
        </v-btn>
      </div>
    </v-card>

    <!-- Paso 4: Detalle "Otra razón" -->
    <v-card v-else-if="paso === 4" class="rounded-xl">
      <div class="d-flex align-center pa-2">
        <v-btn icon small @click="paso = 2">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
      </div>
      <div class="px-5 pb-5">
        <h3 class="font-weight-bold mb-1">Cuéntanos más</h3>
        <p class="text-body-2 grey--text text--darken-1 mb-4">
          Tu opinión nos ayuda a mejorar.
        </p>
        <p class="text-body-2 font-weight-medium mb-2">
          ¿Qué te habría ayudado a terminar?
        </p>
        <v-textarea
          v-model="otraRazonDetalle"
          outlined
          rows="4"
          maxlength="200"
          counter
          placeholder="Escribe aquí (opcional)"
          hide-details="auto"
        />
        <v-btn
          block
          color="success"
          large
          rounded
          elevation="0"
          class="mt-5 font-weight-bold text-capitalize"
          @click="enviarEncuesta"
        >
          Enviar
        </v-btn>
      </div>
    </v-card>

    <!-- Paso 3: Confirmación -->
    <v-card v-else-if="paso === 3" class="pa-6 text-center rounded-xl">
      <div class="d-flex justify-center mb-4">
        <v-avatar size="80" color="success lighten-4">
          <v-icon size="48" color="success">mdi-check</v-icon>
        </v-avatar>
      </div>
      <h2 class="font-weight-bold mb-2" style="font-size: 1.2rem">¡Gracias!</h2>
      <p class="grey--text text--darken-1 text-body-2 mb-1">
        Tu respuesta nos ayuda a mejorar la experiencia para ti y para más
        personas.
      </p>
      <p
        v-if="!modoPreFlujo"
        class="grey--text text--lighten-1 text-caption mb-6"
      >
        <i>Tu avance quedó guardado por si decides regresar.</i>
      </p>
      <v-btn
        block
        color="success"
        large
        rounded
        elevation="0"
        class="font-weight-bold text-capitalize"
        @click="confirmarSalida"
      >
        Volver al inicio
      </v-btn>
    </v-card>
  </v-dialog>
</template>

<script>
import creditoService from '@/servicios/credito'

export default {
  name: 'EncuestaSalida',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    modoPreFlujo: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    paso: 1,
    modo: null,
    razonSeleccionada: null,
    otraRazonDetalle: '',
    razones: [
      'No tenía los documentos a la mano',
      'Proceso muy largo o complicado',
      'No entendí qué me pedían',
      'No me sentí cómodo con la info',
      'Condiciones no eran lo esperado',
      'Tuve un problema técnico',
      'Solo exploraba, no era el momento',
      'Encontré mejor opción',
      'Otra razón',
    ],
  }),
  computed: {
    pasoActual() {
      const info = this.$store.getters['guia/obtenerInfoGuia']
      return info && info.paso ? info.paso : '—'
    },
    totalPasos() {
      const info = this.$store.getters['guia/obtenerInfoGuia']
      if (!info || !info.descripcionPasoCompleta) return 9
      const match = info.descripcionPasoCompleta.match(/de (\d+)/)
      return match ? parseInt(match[1]) : 9
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.resetear()
        if (this.modoPreFlujo) this.paso = 2
      }
    },
  },
  methods: {
    seleccionarModo(modo) {
      this.modo = modo
      this.paso = 2
    },
    enviarEncuesta() {
      const folioDigital = this.$store.getters['credito/obtenerFolioDigital']
      if (folioDigital) {
        creditoService
          .guardarEncuestaCancelacion({
            folioDigital,
            motivo_cancelacion: this.razonSeleccionada,
            otra_razon_detalle: this.otraRazonDetalle || null,
            comentario_ayuda: null,
          })
          .catch(() => {})
      }
      this.paso = 3
    },
    confirmarSalida() {
      if (this.modoPreFlujo) {
        this.$emit('input', false)
        this.resetear()
      } else {
        this.$emit('confirmar-salida')
      }
    },
    cerrar() {
      this.$emit('input', false)
      this.resetear()
    },
    resetear() {
      this.paso = 1
      this.modo = null
      this.razonSeleccionada = null
      this.otraRazonDetalle = ''
    },
  },
}
</script>
